import type { BusinessFlowType } from '@/shared/otp';
import { useRecaptchaV3 } from './useRecaptchaV3';
import { useRecaptchaV2 } from './useRecaptchaV2';
import type {
  RetryOptions,
  UseRecaptchaReturn,
  ApiError,
} from '../types/recaptcha';
import {
  RECAPTCHA_FALLBACK_REASON,
  RECAPTCHA_V2_SITE_KEY,
} from '../constants/recaptcha.constant';
import { waitForRecaptchaContainer } from '../utils/recaptcha.util';

export function useRecaptcha(): UseRecaptchaReturn {
  const v3 = useRecaptchaV3();
  const v2 = useRecaptchaV2(RECAPTCHA_V2_SITE_KEY);

  const getRecaptchaAction = (businessFlow: BusinessFlowType): string =>
    businessFlow;

  const executeV2 = async (options: RetryOptions): Promise<string> => {
    if (!options.containerId) return '';

    await Promise.resolve(options.onV2Show?.());
    await waitForRecaptchaContainer();

    const v2Token = await v2.execute(options.containerId);
    if (v2Token) {
      await Promise.resolve(options.onV2Hide?.());
      v2.cleanup();
    }
    return v2Token || '';
  };

  const execute = async (
    businessFlow: BusinessFlowType,
    options: RetryOptions = {}
  ): Promise<string> => {
    if (options.forceV2Only) {
      return executeV2(options);
    }

    const action = getRecaptchaAction(businessFlow);
    const v3Token = await v3.execute(action, options);
    if (v3Token) return v3Token;

    if (!options.containerId) return '';

    return executeV2(options);
  };

  const executeRecaptchaFlow = async <T>(
    businessFlow: BusinessFlowType,
    apiCall: (token: string, version: 'V2' | 'V3') => Promise<T>,
    options: RetryOptions = {}
  ): Promise<T> => {
    if (options.forceV2Only) {
      if (!options.containerId) {
        throw new Error('[recaptcha] containerId is required when V2-only mode is enabled');
      }

      const v2Token = await executeV2(options);
      if (!v2Token) {
        throw new Error('[recaptcha] V2 token is empty');
      }

      return apiCall(v2Token, 'V2');
    }

    const action = getRecaptchaAction(businessFlow);
    try {
      const v3Token = await v3.execute(action, options);

      if (!v3Token) {
        if (!options.containerId) {
          throw new Error('[recaptcha] V3 token is empty and no V2 container is configured');
        }

        const v2Token = await executeV2(options);
        if (!v2Token) {
          throw new Error('[recaptcha] V2 token is empty');
        }

        return apiCall(v2Token, 'V2');
      }

      return await apiCall(v3Token, 'V3');
    } catch (error: unknown) {
      const apiError = error as ApiError;
      const reason = apiError.response?.data?.reason || '';
      const shouldFallback = reason === RECAPTCHA_FALLBACK_REASON;
      if (!shouldFallback) {
        throw error;
      }

      if (!options.containerId) {
        throw error;
      }

      const v2Token = await executeV2(options);
      if (!v2Token) {
        throw error;
      }

      return apiCall(v2Token, 'V2');
    }
  };

  return { execute, executeRecaptchaFlow, cleanupV2: () => v2.cleanup() };
}
